import { useCallback, useEffect } from 'react';
import type { useChat } from '@ai-sdk/react';
import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  isChatStreamPendingResumeError,
  shouldClearChatStreamResumeFlag,
  shouldDisplayChatStreamError,
} from '@terra/shared-utils';
import type { ChatRef } from '@terra/ui-design-system';
import { useChatInstancesStore } from '../store/chatInstancesStore';
import { useBackgroundExecutionStore } from '../store/backgroundExecutionStore';
import {
  singleFlightTrackedChatResume,
  useChatStreamResumeStore,
} from '../store/chat-stream-resume-store';
import { settleChatStreamResumeIdle } from '../store/settle-chat-stream-resume';
import { terraProxyApiClient } from '../clients/testerClient';
import { resolveSubagentAbortBind } from '../utils/subagentAbortBind';
import { rehydrateAfterFailedSubagentAbort } from './useBackgroundSubagentLifecycle';
import { useStreamRecovery } from './useStreamRecovery';
import { abortChatInstanceTurn } from './abort-chat-instance-turn';
import {
  beginCopilotGraphTurn,
  prepareChatUserTurn,
  sendCopilotGraphMessage,
} from './prepare-chat-user-turn';
import {
  formatChatStreamErrorMessage,
  isRecoverableStreamError,
} from '../utils/chatNetworkErrors';
import { observeChatSendDelivery } from '../utils/chatSendDelivery';

export function useChatInstanceStreamActions({
  chatId,
  targetId,
  targetType,
  status,
  streamError,
  sendChatMessage,
  resumeStream,
  stop,
  regenerate,
  setMessages,
  messagesRef,
  sendErrorRef,
  intentionalAbortRef,
  pauseQueueDrainAfterAbortRef,
  chatComponentRef,
}: {
  chatId: string;
  targetId: string | undefined;
  targetType: string | undefined;
  status: 'streaming' | 'submitted' | 'ready' | 'error';
  streamError: Error | undefined;
  sendChatMessage: ReturnType<typeof useChat<TerraUIMessage>>['sendMessage'];
  resumeStream: () => Promise<void>;
  stop: () => Promise<void>;
  regenerate: () => Promise<void>;
  setMessages: (messages: TerraUIMessage[]) => void;
  messagesRef: React.RefObject<TerraUIMessage[]>;
  sendErrorRef: React.RefObject<Error | undefined>;
  intentionalAbortRef: React.RefObject<boolean>;
  pauseQueueDrainAfterAbortRef: React.RefObject<boolean>;
  chatComponentRef?: React.RefObject<ChatRef | null>;
}) {
  const { updateConfig } = useChatInstancesStore();
  const {
    suppressResume: suppressNeoResume,
    clearResumeSuppression: clearNeoResumeSuppression,
    suppressComposerLoading,
    clearComposerLoadingSuppression,
    clearPendingExecutionsForAgentThread:
      clearPendingBackgroundExecutionsForAgentThread,
  } = useBackgroundExecutionStore();

  const isResumingStream = useChatStreamResumeStore((state) =>
    state.resumingChatIds.has(chatId),
  );

  const resumeActiveStream = useCallback(() => {
    useChatStreamResumeStore.getState().setStreamResuming(chatId, true);

    return singleFlightTrackedChatResume({ chatId, resume: resumeStream });
  }, [chatId, resumeStream]);

  const { cancelScheduledRecovery } = useStreamRecovery({
    error: streamError,
    status,
    resumeStream: resumeActiveStream,
    intentionalAbortRef,
  });

  useEffect(() => {
    if (
      !streamError ||
      !shouldDisplayChatStreamError({
        hasError: true,
        isResumingStream,
        status,
        isRecoverableError: isRecoverableStreamError(streamError),
      })
    ) {
      chatComponentRef?.current?.setErrorMessage(null);

      return;
    }

    chatComponentRef?.current?.setErrorMessage(
      formatChatStreamErrorMessage(streamError),
    );
  }, [chatComponentRef, isResumingStream, status, streamError]);

  const sendGraphMessage = useCallback(
    (...args: Parameters<typeof sendChatMessage>) =>
      sendCopilotGraphMessage({
        chatId,
        send: ({ markStarted }) => {
          markStarted();

          return sendChatMessage(...args);
        },
      }),
    [chatId, sendChatMessage],
  );

  const sendUserMessageWithResult = useCallback(
    async (
      userMessage: TerraUIMessage,
      { onPending }: { onPending?: () => void } = {},
    ): Promise<boolean> => {
      sendErrorRef.current = undefined;
      const chatInstance = useChatInstancesStore.getState().chats.get(chatId);
      const registerStatusCallback = chatInstance?.['~registerStatusCallback'];
      const delivery = observeChatSendDelivery({
        chatId,
        subscribeToLiveStatus:
          chatInstance && typeof registerStatusCallback === 'function'
            ? (onStatus) =>
                registerStatusCallback(() => {
                  onStatus(chatInstance.status);
                })
            : undefined,
      });

      try {
        await sendGraphMessage(userMessage);
      } catch (sendError: unknown) {
        if (isChatStreamPendingResumeError(sendError)) {
          onPending?.();

          return true;
        }

        console.error('Error sending message:', sendError);

        return delivery.wasAcceptedByServer();
      } finally {
        delivery.stop();
      }

      if (sendErrorRef.current) {
        return delivery.wasAcceptedByServer();
      }

      if (useChatInstancesStore.getState().getChatStatus(chatId) === 'error') {
        return delivery.wasAcceptedByServer();
      }

      return true;
    },
    [chatId, sendErrorRef, sendGraphMessage],
  );

  const onResumeStreamSettled = useCallback(
    async (response: Response) => {
      try {
        await settleChatStreamResumeIdle({
          response,
          getMessages: () => messagesRef.current,
          threadId: chatId,
          setMessages,
        });
      } finally {
        if (
          shouldClearChatStreamResumeFlag({ resumeStatus: response.status })
        ) {
          useChatStreamResumeStore.getState().setStreamResuming(chatId, false);
        }
      }
    },
    [chatId, messagesRef, setMessages],
  );

  useEffect(() => {
    const configs = useChatInstancesStore.getState().configs;

    if (configs.has(chatId)) {
      updateConfig(chatId, {
        sendMessage: sendChatMessage,
        resumeStream: resumeActiveStream,
        setMessages,
        onResumeStreamSettled,
        intentionalAbortRef,
      });
    }
  }, [
    chatId,
    sendChatMessage,
    resumeActiveStream,
    setMessages,
    onResumeStreamSettled,
    status,
    updateConfig,
    intentionalAbortRef,
  ]);

  const handleAbort = useCallback(
    async ({ pauseQueueDrain = true }: { pauseQueueDrain?: boolean } = {}) => {
      await abortChatInstanceTurn({
        chatId,
        targetId,
        pauseQueueDrain,
        intentionalAbortRef,
        pauseQueueDrainAfterAbortRef,
        messagesRef,
        cancelScheduledRecovery,
        stop,
        setMessages,
        suppressNeoResume,
        suppressComposerLoading,
        clearPendingBackgroundExecutionsForAgentThread,
      });
    },
    [
      targetId,
      chatId,
      cancelScheduledRecovery,
      clearPendingBackgroundExecutionsForAgentThread,
      intentionalAbortRef,
      messagesRef,
      pauseQueueDrainAfterAbortRef,
      setMessages,
      stop,
      suppressComposerLoading,
      suppressNeoResume,
    ],
  );

  const handleAbortSubagent = useCallback(
    async ({ toolCallId }: { toolCallId: string }) => {
      if (!chatId) {
        return;
      }

      const bind = resolveSubagentAbortBind({
        execution: useBackgroundExecutionStore
          .getState()
          .getPendingExecution(toolCallId),
        currentTarget: { targetId, targetType },
      });

      try {
        await terraProxyApiClient.abortSubagent({
          threadId: chatId,
          ...bind,
          toolCallId,
        });
        useBackgroundExecutionStore.getState().markSubagentTerminal({
          toolCallId,
          terminalStatus: 'aborted',
        });
      } catch (abortError) {
        rehydrateAfterFailedSubagentAbort({ chatId, error: abortError });
      }
    },
    [chatId, targetId, targetType],
  );

  useEffect(() => {
    const configs = useChatInstancesStore.getState().configs;

    if (configs.has(chatId)) {
      updateConfig(chatId, {
        abortStream: async () => {
          await handleAbort();
        },
      });
    }
  }, [chatId, handleAbort, updateConfig]);

  const handleRegenerate = useCallback(() => {
    cancelScheduledRecovery();
    prepareChatUserTurn({
      chatId,
      clearNeoResumeSuppression,
      clearComposerLoadingSuppression,
    });
    beginCopilotGraphTurn(chatId);
    void regenerate();
  }, [
    cancelScheduledRecovery,
    chatId,
    clearComposerLoadingSuppression,
    clearNeoResumeSuppression,
    regenerate,
  ]);

  return {
    isResumingStream,
    resumeActiveStream,
    cancelScheduledRecovery,
    sendGraphMessage,
    sendUserMessageWithResult,
    handleAbort,
    handleAbortSubagent,
    handleRegenerate,
    clearNeoResumeSuppression,
    clearComposerLoadingSuppression,
  };
}
